<script>
import { ref } from 'vue';
import AlertResponza from '../../../../components-next/responza/AlertResponza.vue';

export default {
  name: 'AlertResponzaTest',
  components: {
    AlertResponza,
  },
  setup() {
    const lastAction = ref('');

    // Success Alert
    const successAlert = ref({
      show: false,
      title: 'Sucesso!',
      message:
        'Você fez o componente corretamente, agora use em todo o código para padronizar.',
    });

    // Warning Alert
    const warningAlert = ref({
      show: false,
      title: 'Atenção',
      message:
        'Esta ação pode demorar alguns minutos para ser processada. Por favor, aguarde.',
    });

    // Warning Confirmation Alert
    const warningConfirmationAlert = ref({
      show: false,
      title: 'Deseja sair sem salvar?',
      message:
        'Você tem alterações não salvas. Se sair agora, todas as modificações serão perdidas.',
    });

    // Error Alert
    const errorAlert = ref({
      show: false,
      title: 'Erro ao processar',
      message:
        'Ocorreu um erro ao tentar conectar. Verifique suas credenciais e tente novamente.',
      buttonText: '',
      action: null,
    });

    // Confirmation Alert
    const confirmationAlert = ref({
      show: false,
      title: 'Confirmar exclusão',
      message:
        'Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.',
    });

    // Show functions
    const showSuccessAlert = () => {
      successAlert.value.show = true;
      lastAction.value = 'Success alert shown';
    };

    const showWarningAlert = () => {
      warningAlert.value.show = true;
      lastAction.value = 'Warning alert shown';
    };

    const showWarningConfirmationAlert = () => {
      warningConfirmationAlert.value.show = true;
      lastAction.value = 'Warning confirmation alert shown';
    };

    const showErrorAlert = () => {
      errorAlert.value.buttonText = '';
      errorAlert.value.action = null;
      errorAlert.value.show = true;
      lastAction.value = 'Error alert shown (default button)';
    };

    const showCustomErrorAlert = () => {
      errorAlert.value.buttonText = 'Tentar Novamente';
      errorAlert.value.action = () => {
        lastAction.value = 'Custom error action executed - Retry attempted';
        console.log('Retrying action...');
      };
      errorAlert.value.show = true;
      lastAction.value = 'Error alert shown (custom button)';
    };

    const showConfirmationAlert = () => {
      confirmationAlert.value.show = true;
      lastAction.value = 'Confirmation alert shown';
    };

    // Handle confirmation actions
    const handleConfirm = () => {
      lastAction.value = 'User clicked "Confirmar" - Action confirmed!';
      confirmationAlert.value.show = false;
    };

    const handleCancel = () => {
      lastAction.value = 'User clicked "Cancelar" - Action cancelled';
      confirmationAlert.value.show = false;
    };

    // Handle warning confirmation actions
    const handleWarningConfirm = () => {
      lastAction.value = 'User clicked "Sair sem salvar" - Warning confirmed!';
      warningConfirmationAlert.value.show = false;
    };

    const handleWarningCancel = () => {
      lastAction.value = 'User clicked "Cancelar" - Warning cancelled';
      warningConfirmationAlert.value.show = false;
    };

    return {
      lastAction,
      successAlert,
      warningAlert,
      warningConfirmationAlert,
      errorAlert,
      confirmationAlert,
      showSuccessAlert,
      showWarningAlert,
      showWarningConfirmationAlert,
      showErrorAlert,
      showCustomErrorAlert,
      showConfirmationAlert,
      handleConfirm,
      handleCancel,
      handleWarningConfirm,
      handleWarningCancel,
    };
  },
};
</script>

<template>
  <div class="column content-box">
    <div class="header-container">
      <h1 class="page-title">Test AlertResponza Modal</h1>
      <p class="page-subtitle">
        Click the buttons below to test different alert types
      </p>
    </div>

    <div class="test-buttons-container">
      <!-- Success Alert Test -->
      <div class="test-section">
        <h3 class="section-title">Success Alert</h3>
        <button class="test-button success" @click="showSuccessAlert">
          Show Success Alert
        </button>
      </div>

      <!-- Warning Alert Test -->
      <div class="test-section">
        <h3 class="section-title">Warning Alert (Single Button)</h3>
        <button class="test-button warning" @click="showWarningAlert">
          Show Warning Alert
        </button>
      </div>

      <!-- Warning Confirmation Alert Test -->
      <div class="test-section">
        <h3 class="section-title">Warning Confirmation Alert (Two Buttons)</h3>
        <button class="test-button warning" @click="showWarningConfirmationAlert">
          Show Warning Confirmation Alert
        </button>
      </div>

      <!-- Error Alert Test -->
      <div class="test-section">
        <h3 class="section-title">Error Alert</h3>
        <button class="test-button error" @click="showErrorAlert">
          Show Error Alert
        </button>
        <button class="test-button error ml-2" @click="showCustomErrorAlert">
          Show Custom Error Alert
        </button>
      </div>

      <!-- Confirmation Alert Test -->
      <div class="test-section">
        <h3 class="section-title">Confirmation Alert</h3>
        <button class="test-button confirmation" @click="showConfirmationAlert">
          Show Confirmation Alert
        </button>
      </div>

      <!-- Test Results -->
      <div v-if="lastAction" class="test-results">
        <h3 class="section-title">Last Action Result:</h3>
        <p class="result-text">{{ lastAction }}</p>
      </div>
    </div>

    <!-- Alert Modals -->
    <AlertResponza
      :show="successAlert.show"
      type="success"
      :title="successAlert.title"
      :message="successAlert.message"
      @close="successAlert.show = false"
    />

    <AlertResponza
      :show="warningAlert.show"
      type="warn"
      :title="warningAlert.title"
      :message="warningAlert.message"
      @close="warningAlert.show = false"
    />

    <AlertResponza
      :show="warningConfirmationAlert.show"
      type="warning-confirmation"
      :title="warningConfirmationAlert.title"
      :message="warningConfirmationAlert.message"
      confirm-text="Sair sem salvar"
      @confirm="handleWarningConfirm"
      @cancel="handleWarningCancel"
      @close="handleWarningCancel"
    />

    <AlertResponza
      :show="errorAlert.show"
      type="error"
      :title="errorAlert.title"
      :message="errorAlert.message"
      :error-button-text="errorAlert.buttonText"
      :on-error-action="errorAlert.action"
      @close="errorAlert.show = false"
    />

    <AlertResponza
      :show="confirmationAlert.show"
      type="confirmation"
      :title="confirmationAlert.title"
      :message="confirmationAlert.message"
      @confirm="handleConfirm"
      @cancel="handleCancel"
      @close="confirmationAlert.show = false"
    />
  </div>
</template>

<style scoped>
.column.content-box {
  padding: 2rem;
  padding-bottom: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.header-container {
  margin-bottom: 3rem;
  text-align: center;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: var(--s-900);
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.1rem;
  color: var(--s-600);
}

.test-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.test-section {
  background: var(--white);
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 1rem;
  color: var(--s-700);
}

.test-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.test-button.success {
  background-color: var(--g-600);
}

.test-button.success:hover {
  background-color: var(--g-700);
}

.test-button.warning {
  background-color: var(--y-600);
}

.test-button.warning:hover {
  background-color: var(--y-700);
}

.test-button.error {
  background-color: var(--r-600);
}

.test-button.error:hover {
  background-color: var(--r-700);
}

.test-button.confirmation {
  background-color: var(--w-600);
}

.test-button.confirmation:hover {
  background-color: var(--w-700);
}

.ml-2 {
  margin-left: 0.5rem;
}

.test-results {
  background-color: var(--s-50);
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid var(--s-200);
  margin-bottom: 2rem;
}

.result-text {
  font-size: 1rem;
  color: var(--s-700);
  font-family: monospace;
}
</style>
